// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import '@styles/webdesktop/stylesheets/contextMenu.scss';
import React, { useRef } from 'react';
import { Popover } from '../Popover/Popover';
import { useContextMenu } from './useContextMenu';
import { ContextMenuItem } from './ContextMenuItems/ContextMenuItem';
import { ContextMenuItemSeparator } from './ContextMenuItems/ContextMenuItemSeparator';
import { ContextMenuItemAccount } from './ContextMenuItems/ContextMenuItemAccount';
import { ContextMenuItemActions } from './ContextMenuItems/ContextMenuItemActions';
import { useAccountContextMenu } from './useAccountContextMenu';
export const ContextMenu = React.forwardRef((_a, ref) => {
    var { items, position, onClose, minWidth, maxWidth, hasParent, openedMenuRefs } = _a, otherProps = __rest(_a, ["items", "position", "onClose", "minWidth", "maxWidth", "hasParent", "openedMenuRefs"]);
    const internalRef = useRef(null);
    const menuRef = (ref !== null && ref !== void 0 ? ref : internalRef);
    const subMenuRef = useRef(null);
    // Root menu creates the shared array; submenus receive and reuse the same instance via props.
    const internalExcludeMenuRefs = useRef([]);
    const excludeMenuRefs = openedMenuRefs !== null && openedMenuRefs !== void 0 ? openedMenuRefs : internalExcludeMenuRefs.current;
    const { subMenuProps, hasLeftOffset, hasRightOffset, onItemPointerEnter } = useContextMenu({
        menuRef,
        menuProps: { items, position, onClose, minWidth, maxWidth, hasParent },
        openedMenuRefs: excludeMenuRefs
    });
    const { hasInstrumentTypeOffset } = useAccountContextMenu({ items });
    const renderItem = (item) => {
        switch (item.type) {
            case 'separator':
                return React.createElement(ContextMenuItemSeparator, { key: item.id, "data-testid": item.id });
            case 'item':
                return (React.createElement(ContextMenuItem, { key: item.id, text: item.text, hasLeftOffset: hasLeftOffset, hasRightOffset: hasRightOffset, hasSubitems: isValidArray(item.subitems), icon: item.icon, disabled: item.disabled, checked: item.checked, isBeta: item.isBeta, onPointerEnter: (element) => {
                        onItemPointerEnter(item, element);
                    }, onClick: () => {
                        var _a;
                        (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, item);
                        onClose();
                    } }));
            case 'account':
                return (React.createElement(ContextMenuItemAccount, { key: item.id, accountName: item.accountName, accountMode: item.accountMode, hasInstrumentTypeOffset: hasInstrumentTypeOffset, instrumentType: item.instrumentType, disabled: item.disabled, checked: item.checked, onClick: () => {
                        var _a;
                        (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, item);
                        onClose();
                    }, "data-testid": item.id }));
            case 'actions':
                return (React.createElement(ContextMenuItemActions, { key: item.id, text: item.text, actions: item.actions.map((action) => (Object.assign(Object.assign({}, action), { onClick: () => {
                            var _a;
                            (_a = action.onClick) === null || _a === void 0 ? void 0 : _a.call(action);
                            onClose();
                        } }))), checked: item.checked, onClick: () => {
                        var _a;
                        (_a = item.onClick) === null || _a === void 0 ? void 0 : _a.call(item, item);
                        onClose();
                    }, "data-testid": item.id }));
        }
    };
    if (items.length === 0) {
        return null;
    }
    const accountItems = items.filter((item) => item.type === 'account');
    const otherItems = items.filter((item) => item.type !== 'account');
    return (React.createElement(React.Fragment, null,
        React.createElement(Popover, { ref: menuRef, className: "context-menu-popover", top: position.y, left: position.x, minWidth: minWidth, maxWidth: maxWidth, excludeRefs: excludeMenuRefs, onOutsideClick: onClose },
            React.createElement("ul", Object.assign({ className: "context-menu-list", role: "menu" /* NOSONAR S6842 - ul as menu with role is intentional */ }, otherProps),
                isValidArray(accountItems) && (React.createElement("fieldset", { className: "context-menu-list-accounts", role: "group" }, accountItems.map(renderItem))),
                otherItems.map(renderItem))),
        !isNullOrUndefined(subMenuProps) && React.createElement(ContextMenu, Object.assign({ ref: subMenuRef }, subMenuProps))));
});
